

import * as React from "react";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
} from "lucide-react";
import {
  DayPicker,
  type DayPickerProps,
  type DropdownProps,
} from "react-day-picker";

import { Button, buttonVariants } from "./button";
import { cn } from "@sunfish/lib/utils";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";

export type CalendarProps = DayPickerProps;

function CustomSelectDropdown(props: DropdownProps) {
  const { options, value, onChange, "aria-label": ariaLabel } = props;

  const handleValueChange = (newValue: string) => {
    if (onChange) {
      const syntheticEvent = {
        target: {
          value: newValue,
        },
      } as React.ChangeEvent<HTMLSelectElement>;

      onChange(syntheticEvent);
    }
  };

  return (
    <Select value={value?.toString()} onValueChange={handleValueChange}>
      <SelectTrigger aria-label={ariaLabel}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {options?.map((option) => (
            <SelectItem
              key={option.value}
              value={option.value.toString()}
              disabled={option.disabled}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        months: "relative flex flex-col sm:flex-row space-y-4 sm:space-y-0",
        month: "space-y-4",
        nav: "flex items-center justify-between absolute w-full z-10 px-1",
        button_previous: cn(
          buttonVariants({
            variant: "outline",
            className:
              "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
          }),
        ),
        button_next: cn(
          buttonVariants({
            variant: "outline",
            className:
              "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
          }),
        ),
        month_caption: "relative flex justify-center items-center h-7",
        caption_label: "text-sm font-medium hidden",
        dropdowns: "relative z-20 flex items-center gap-2",
        month_grid: "border-collapse space-y-1",
        weekdays: "flex gap-2",
        weekday: "text-muted-foreground w-[36px] font-normal text-xs",
        weeks: "",
        week: "flex mt-2 gap-2",
        day: "p-0",
        outside: "bg-accent/40",
        ...classNames,
      }}
      components={{
        Dropdown: CustomSelectDropdown,
        DayButton({ modifiers, className, ...buttonProps }) {
          return (
            <Button
              variant={"ghost"}
              className={cn(
                className,
                "h-9 w-9 p-0 font-normal",
                modifiers?.today && "bg-accent text-accent-foreground",
                modifiers?.selected &&
                  "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                modifiers?.focused &&
                  "bg-blue-800 text-white border-blue-800 hover:bg-blue-700 hover:text-white focus:bg-blue-700 focus:text-white",
                modifiers?.outside &&
                  "text-muted-foreground opacity-50 pointer-events-none",
                modifiers?.disabled && "opacity-50 text-muted-foreground",
                modifiers?.hidden && "invisible",
                modifiers.range_middle &&
                  "bg-primary text-primary-foreground opacity-60 hover:bg-accent hover:text-accent-foreground rounded-none last:rounded-e-md first:rounded-s-md",
                modifiers.outside &&
                  modifiers.selected &&
                  "bg-accent/40 text-muted-foreground",
              )}
              {...buttonProps}
              aria-selected={modifiers.selected || buttonProps["aria-selected"]}
              aria-disabled={modifiers.disabled || buttonProps["aria-disabled"]}
              aria-hidden={modifiers.hidden || buttonProps["aria-hidden"]}
            />
          );
        },
        Chevron({ orientation, disabled, className }) {
          const Component =
            orientation === "left"
              ? ChevronLeft
              : orientation === "right"
                ? ChevronRight
                : orientation === "up"
                  ? ChevronUp
                  : ChevronDown;

          return (
            <Component
              className={cn("w-4 h-4", className)}
              aria-disabled={disabled}
            />
          );
        },
      }}
      {...props}
    />
  );
}

Calendar.displayName = "Calendar";

export { Calendar };
